import { AzureDataBricksLinkedService } from './../linkedservice/azure-data-bricks.linkedservice';
import { Model } from 'app/model/backendmodel';
import {
    IModel,
    IAttribute,
    ModelConstants,
    ParameterValue,
    IViolation,
    sanitizeAndFormatString,
    ParameterConfig,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config-string.util';
import { Collection } from 'app/model/base/annotation';
import { IKeyValPair } from 'app/interfaces/IKeyValPair';
import { ILibraryVal } from 'app/interfaces/ILibraryVal';
import { ParameterConfigString } from 'app/model/pipeline/parameter-config-string';
import { format } from 'app/shared/utils/string.util';
import { DatabricksParameter } from './databricks-parameter';
import { Copier } from 'app/model/base/copier';

@Metamodel.model({
    name: ModelConstants.notebookActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.Notebook_DisplayText),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.Notebook_DisplayText)
    ],
    referable: true
})
export class NotebookActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly NOTEBOOK_PATH: IAttribute;
    public static readonly MAIN_CLASS_NAME: IAttribute;
    public static readonly PYTHON_FILE: IAttribute;
    public static readonly LIBRARIES: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly TYPE: IAttribute;
    public static readonly JOBID: IAttribute;

    //FABRIC CLUSTER ATTRIBUTES
    public static readonly EXISTING_CLUSTER_ID: IAttribute;
    public static readonly NODE_TYPE: IAttribute;
    public static readonly NUM_WORKER: IAttribute;
    public static readonly SPARK_CONF: IAttribute;
    public static readonly ENV_VARIABLES: IAttribute;
    public static readonly CUSTOM_TAGS: IAttribute;
    public static readonly VERSION: IAttribute;
    public static readonly DESTINATION_TYPE: IAttribute;
    public static readonly DBFS_DESTINATION: IAttribute;
    public static readonly INIT_SCRIPTS: IAttribute;
    public static readonly DRIVER_NODE_TYPE: IAttribute;
    public static readonly INSTANCE_POOL_ID: IAttribute;
    public static readonly ADDITIONAL_CLUSTER_ATTRIBUTES: IAttribute;
    public static readonly CLUSTER_OPTION: IAttribute;
    public static readonly POLICY_ID: IAttribute;
    public static readonly DATA_SECURITY_MODE: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ModelName_Cluster,
        constraint: {
            validator: NotebookActivity.validateClusterMode
        }
    })
    public useClusterMode: 'new' | 'exist' | 'pool' = 'new';

    @Metamodel.attribute({
        display: stringResources.ModelName_ExistingClusterId,
        templatable: true,
        association: 'EXISTING_CLUSTER_ID'
    })
    public existingClusterId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_InstancePoolId,
        templatable: true,
        association: 'INSTANCE_POOL_ID'
    })
    public instancePoolId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterVersion,
        templatable: true,
        association: 'VERSION'
    })
    public newClusterVersion?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterNumOfWorker,
        templatable: true,
        association: 'NUM_WORKER'
    })
    public newClusterNumOfWorker?: string = '1';

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterNodeType,
        templatable: true,
        association: 'NODE_TYPE'
    })
    public newClusterNodeType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterSparkConf,
        templatable: true,
        association: 'SPARK_CONF'
    })
    public newClusterSparkConf?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterSparkEnvVars,
        templatable: true,
        association: 'ENV_VARIABLES'
    })
    public newClusterSparkEnvVars?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterCustomTags,
        templatable: true,
        association: 'CUSTOM_TAGS'
    })
    public newClusterCustomTags?: ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterInitScripts,
        templatable: true,
        association: 'INIT_SCRIPTS'
    })
    public newClusterInitScripts?: DatabricksParameter[] | ParameterConfig[];

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterDriverNodeType,
        templatable: true,
        association: 'DRIVER_NODE_TYPE'
    })
    public newClusterDriverNodeType?: string;

    @Metamodel.attribute({
        display: stringResources.DataBricks_Cluster_Policy,
        templatable: false,
        association: 'POLICY_ID'
    })
    public policyId: string = undefined;

    @Metamodel.attribute({
        display: stringResources.DestinationLabel,
        templatable: true,
        association: 'DESTINATION_TYPE'
    })
    public newClusterLogDestinationType?: string = 'Dbfs'

    @Metamodel.attribute({
        display: stringResources.ModelName_NewClusterLogDestination,
        templatable: true,
        association: 'DBFS_DESTINATION'
    })
    public newClusterLogDestination?: string;

    @Metamodel.attribute({
        display: stringResources.DatabricksClusterAdditionalSettings,
        templatable: false,
        association: 'ADDITIONAL_CLUSTER_ATTRIBUTES'
    })
    public newClusterAttributes: Model.INewClusterAttributes = {
        firstOnDemand: 1,
        availability: 'OnDemand',
        spotBidMaxPrice: -1
    };

    @Metamodel.attribute({
        display: stringResources.WorkerOptions,
        templatable: false,
        association: 'CLUSTER_OPTION'
    })
    public clusterOption: string = 'Fixed';

    //FABRIC COMBINED ATTRIBUTES

    @Metamodel.attribute({
        display: stringResources.Type,
        constraint: {
            required: true,
            validator: NotebookActivity.validateType
        },
        association: 'TYPE'
    })
    public type: string = 'Notebook';

    @Metamodel.attribute({
        display: stringResources.MainClassNameLabel,
        constraint: {
            required: false
        },
        templatable: true,
        association: 'MAIN_CLASS_NAME'
    })
    public mainClassName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_PythonFile,
        constraint: {
            required: false
        },
        templatable: true,
        association: 'PYTHON_FILE'
    })
    public pythonFile?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_NotebookPath,
        constraint: {
            required: false
        },
        templatable: true,
        association: 'NOTEBOOK_PATH'
    })
    public notebookPath: string;

    @Metamodel.attribute({
        display: stringResources.JobActivity_ModelName,
        constraint: {
            required: false
        },
        templatable: true,
        association: 'JOBID'
    })
    public jobId?: string;

    @Metamodel.attribute({
        display: stringResources.Databricks_JobActivity_JobParameterName,
        constraint: {
            validator: NotebookActivity.validateParameters
        }
    })
    public jobParameters?: ParameterConfigString[];
    
    @Metamodel.attribute({
        display: stringResources.ModelName_BasePrarmters,
        constraint: {
            validator: NotebookActivity.validateParameters
        }
    })
    public parameters?: ParameterConfigString[] | DatabricksParameter[];

    @Metamodel.attribute({
        display: __IS_FABRIC_DPE__
            ? stringResources.ConnectionLabel
            : format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataBricks_DisplayText),
        constraint: {
            required: true,
            validator: NotebookActivity.validateLinkedService
        },
        reference: true,
        type: AzureDataBricksLinkedService.MODEL
    })
    public linkedService?: AzureDataBricksLinkedService;

    /**
     * Parameter value for referenced linked service.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        display: stringResources.ModelName_NotebookLibraries,
        collection: Collection.Array,
        templatable: true,
        association: 'LIBRARIES'
    })
    public libraries?: Array<IKeyValPair<ILibraryVal>> = [];

    @Metamodel.attribute({
        display: stringResources.DataBricks_Cluster_Unity_Catalog_Access_Mode,
        constraint: {
            required: false
        },
        templatable: true,
        association: 'DATA_SECURITY_MODE'
    })
    public dataSecurityMode?: string;

    private static async validateType(dbActivity: NotebookActivity, type: String): Promise<IViolation[] | undefined> {
        if (__IS_FABRIC_DPE__) {
            if (type === 'Notebook') {
                if (
                    stringIsNullOrEmpty(dbActivity.notebookPath) &&
                    stringIsNullOrEmpty(NotebookActivity.NOTEBOOK_PATH.templateValue(dbActivity))
                ) {
                    return [
                        {
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_NotebookPath
                                )
                        }
                    ];
                }
            } else if (type === 'Jar') {
                const violations = [];
                if (
                    stringIsNullOrEmpty(dbActivity.mainClassName) &&
                    stringIsNullOrEmpty(NotebookActivity.MAIN_CLASS_NAME.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.MainClassNameLabel
                            )
                    });
                }
                if (dbActivity && dbActivity.libraries && dbActivity.libraries.length === 0) {
                    violations.push({
                        message: () => stringResources.JarActivity_Libraries_Empty_Error
                    });
                }
                return violations;
            } else if (type === 'Python') {
                if (
                    stringIsNullOrEmpty(dbActivity.pythonFile) &&
                    stringIsNullOrEmpty(NotebookActivity.PYTHON_FILE.templateValue(dbActivity))
                ) {
                    return [
                        {
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_PythonFile
                                )
                        }
                    ];
                }
            } else if (type === 'Job') {
                if (
                    stringIsNullOrEmpty(dbActivity.jobId) &&
                    stringIsNullOrEmpty(NotebookActivity.JOBID.templateValue(dbActivity))
                ) {
                    return [
                        {
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.JobActivity_ModelName
                                )
                        }
                    ];
                }
            }
        } else {
            if (type === 'Notebook') {
                if (
                    stringIsNullOrEmpty(dbActivity.notebookPath) &&
                    stringIsNullOrEmpty(NotebookActivity.NOTEBOOK_PATH.templateValue(dbActivity))
                ) {
                    return [
                        {
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.RequiredTemplate,
                                    stringResources.ModelName_NotebookPath
                                )
                        }
                    ];
                }
            }
        }
        return undefined;
    }

    private static async validateClusterMode(
        dbActivity: NotebookActivity,
        clusterMode: String
    ): Promise<IViolation[] | undefined> {
        if (__IS_FABRIC_DPE__ && dbActivity.type !== 'Job') {
            const violations = [];
            if (clusterMode === 'new') {
                if (
                    stringIsNullOrEmpty(dbActivity.newClusterVersion) &&
                    stringIsNullOrEmpty(NotebookActivity.VERSION.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_NewClusterVersion
                            )
                    });
                }
                if (
                    stringIsNullOrEmpty(dbActivity.newClusterNodeType) &&
                    stringIsNullOrEmpty(NotebookActivity.NODE_TYPE.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_NewClusterNodeType
                            )
                    });
                }
                if (
                    stringIsNullOrEmpty(dbActivity.newClusterNumOfWorker) &&
                    stringIsNullOrEmpty(NotebookActivity.NUM_WORKER.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_NewClusterNumOfWorker
                            )
                    });
                }
            } else if (clusterMode === 'pool') {
                if (
                    stringIsNullOrEmpty(dbActivity.instancePoolId) &&
                    stringIsNullOrEmpty(NotebookActivity.INSTANCE_POOL_ID.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_InstancePoolId
                            )
                    });
                }
                if (
                    stringIsNullOrEmpty(dbActivity.newClusterVersion) &&
                    stringIsNullOrEmpty(NotebookActivity.VERSION.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_NewClusterVersion
                            )
                    });
                }
                if (
                    stringIsNullOrEmpty(dbActivity.newClusterNumOfWorker) &&
                    stringIsNullOrEmpty(NotebookActivity.NUM_WORKER.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_NewClusterNumOfWorker
                            )
                    });
                }
            } else if (clusterMode === 'exist') {
                if (
                    stringIsNullOrEmpty(dbActivity.existingClusterId) &&
                    stringIsNullOrEmpty(NotebookActivity.EXISTING_CLUSTER_ID.templateValue(dbActivity))
                ) {
                    violations.push({
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.RequiredTemplate,
                                stringResources.ModelName_ExistingClusterId
                            )
                    });
                }
            }
            return violations;
        }
        return undefined;
    }

    private static async validateLinkedService(
        dbActivity: NotebookActivity
    ) : Promise<IViolation[] | undefined> {
        if (dbActivity.linkedService?.useClusterMode === 'serverless') {
            return [{
                message: () => stringResources.Databricks_Serverless_Error
            }]
        }
        return undefined;
    }

    private static async validateParameters(
        dbActivity: NotebookActivity,
        parameters?: ParameterConfigString[] | DatabricksParameter[]
    ): Promise<IViolation[] | undefined> {
        if (__IS_FABRIC_DPE__) {
            if (dbActivity.type === 'Notebook' || dbActivity.type === 'Job') {
                return createParameterConfigurationValidator()(dbActivity, parameters as ParameterConfigString[]);
            } else {
                const violations: IViolation[] = [];
                return violations;
            }
        }
        return createParameterConfigurationValidator()(dbActivity, parameters as ParameterConfigString[]);
    }

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public onActivityCloned(clonedActivity: NotebookActivity, context: { inlineDataset?: boolean }): void {
        if (__IS_FABRIC_DPE__) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as AzureDataBricksLinkedService;
            }
        }
    }
}
